import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { tutorApiService } from "../../../services/tutorApi";
import { useGroupListStore, useTutorChatStore } from "../../../store";
import { TutorGroup } from "../../../types/tutor.types";
import { Mode } from "../../../types";

/**
 * useGroupMutations
 *
 * React Query Mutations for Group CRUD operations
 *
 * 기능:
 * - 그룹 생성 (createGroup)
 * - 그룹 제목 수정 (updateGroupTitle)
 * - 그룹 삭제 (deleteGroup)
 *
 * 대체:
 * - useGroupFeature.createGroup
 * - useGroupFeature.updateGroupTitle
 * - useGroupFeature.deleteGroup
 */

interface CreateGroupOptions {
  title: string;
  skipClearHistory?: boolean; // 히스토리 클리어 건너뛰기 (스트리밍 중인 메시지 보존용)
}

interface UpdateGroupOptions {
  groupId: string;
  title: string;
}

interface NavigationState {
  from_chat: "new" | "group" | "storage";
  his_cnt: number;
  is_writed: boolean;
  is_completed: boolean;
}

const TUTOR_URLS = {
  EDIT_GROUP_LIST: process.env.REACT_APP_TUTOR_EDIT_GROUP_LIST,
};

const SOCRATES_URLS = {
  EDIT_GROUP_LIST: process.env.REACT_APP_SOCRATES_EDIT_GROUP_LIST,
};

/**
 * 그룹 생성 Mutation
 */
export const useCreateGroupMutation = (
  mode: Mode,
  pageName: string,
  skipNavigation?: boolean
) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setCurrentGroupId } = useGroupListStore();
  const { clearHistory } = useTutorChatStore();

  return useMutation({
    mutationFn: async (options: CreateGroupOptions): Promise<TutorGroup> => {
      const { title } = options;

      const url =
        mode === Mode.SOCRATES
          ? SOCRATES_URLS.EDIT_GROUP_LIST
          : TUTOR_URLS.EDIT_GROUP_LIST;

      const newGroup: TutorGroup = await tutorApiService.touchAPI("POST", url, {
        title,
      });

      if (!newGroup) {
        throw new Error("Failed to create group");
      }

      return newGroup;
    },
    onSuccess: (newGroup, variables) => {
      // 1. 이전 그룹의 스트리밍 메시지 초기화 (옵션에 따라 건너뛰기 가능)
      if (!variables.skipClearHistory) {
        clearHistory();
      }

      // 2. currentGroupId 설정 (수동 네비게이션 모드가 아닌 경우에만)
      if (!skipNavigation) {
        setCurrentGroupId(newGroup.id);
      }

      // 3. 자동 네비게이션 (수동 네비게이션 모드가 아닌 경우에만)
      if (!skipNavigation) {
        const navState: NavigationState = {
          from_chat: "new",
          his_cnt: 0,
          is_writed: false,
          is_completed: false,
        };

        navigate(`/${pageName}/${newGroup.id}`, { state: navState });
      }

      // 4. 그룹 목록 캐시 무효화 (자동 refetch)
      queryClient.invalidateQueries({ queryKey: ["groups", mode] });
    },
    onError: (error) => {
      console.error("Failed to create group:", error);
    },
  });
};

/**
 * 그룹 제목 수정 Mutation
 */
export const useUpdateGroupMutation = (mode: Mode) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (options: UpdateGroupOptions): Promise<boolean> => {
      const { groupId, title } = options;

      const url =
        mode === Mode.SOCRATES
          ? SOCRATES_URLS.EDIT_GROUP_LIST
          : TUTOR_URLS.EDIT_GROUP_LIST;

      const result = await tutorApiService.touchAPI("PATCH", url, {
        title,
        group_id: Number(groupId),
      });

      if (!result) {
        throw new Error("Failed to update group title");
      }

      return true;
    },
    onSuccess: () => {
      // 그룹 목록 캐시 무효화 (자동 refetch)
      queryClient.invalidateQueries({ queryKey: ["groups", mode] });
    },
    onError: (error) => {
      console.error("Failed to update group title:", error);
    },
  });
};

/**
 * 그룹 삭제 Mutation
 */
export const useDeleteGroupMutation = (mode: Mode) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { currentGroupId, setCurrentGroupId } = useGroupListStore();

  // mode에 따라 라우팅 경로 결정
  const basePath = mode === Mode.TUTOR ? "tutor" : "socrates";

  return useMutation({
    mutationFn: async (groupId: string): Promise<boolean> => {
      const url =
        mode === Mode.SOCRATES
          ? SOCRATES_URLS.EDIT_GROUP_LIST
          : TUTOR_URLS.EDIT_GROUP_LIST;

      const result = await tutorApiService.touchAPI(
        "DELETE",
        url,
        `/${groupId}`
      );

      if (!result) {
        throw new Error("Failed to delete group");
      }

      return true;
    },
    onSuccess: (_, groupId) => {
      // 1. 현재 보고 있던 그룹이면 홈으로 이동
      if (currentGroupId === groupId) {
        navigate(`/${basePath}`);
        setCurrentGroupId(null);
      }

      // 2. 그룹 목록 캐시 무효화 (자동 refetch)
      queryClient.invalidateQueries({ queryKey: ["groups", mode] });

      // 3. Socrates V2 인터뷰 목록 캐시도 무효화
      if (mode === Mode.SOCRATES) {
        queryClient.invalidateQueries({
          queryKey: ["socratesV2", "interviews"],
        });
      }
    },
    onError: (error) => {
      console.error("Failed to delete group:", error);
    },
  });
};
